import React from "react";
import Reveal from "./Reveal";
import { Plane } from "lucide-react";

/*
 * Calendar grid — December 2026
 * Week starts Sunday. Trip runs Dec 3 (Thu) → Dec 13 (Sun).
 * Days before/after the trip are shown muted.
 */

const days = [
  // Row 1: Nov 29 – Dec 5
  { date: 29, label: "29", muted: true },
  { date: 30, label: "30", muted: true },
  { date: 1,  label: "DEC 1", muted: true },
  { date: 2,  label: "2",  muted: true },
  { date: 3,  label: "3",  title: ["Depart USA", "Overnight Flight"] },
  { date: 4,  label: "4",  title: ["Arrive London", "City & Market Tour"], city: "LONDON" },
  { date: 5,  label: "5",  title: ["Market Tours &", "Explore City"], city: "LONDON" },

  // Row 2: Dec 6 – Dec 12
  { date: 6,  label: "6",  title: ["Museum Optional", "Hyde Park Market"], city: "LONDON" },
  { date: 7,  label: "7",  title: ["AM Train", "Evening Market Tour"], city: "BRUSSELS" },
  { date: 8,  label: "8",  title: ["Bruges Day Trip", "Included"], city: "BRUSSELS" },
  { date: 9,  label: "9",  title: ["Museum Optional", "Market Tour"], city: "BRUSSELS" },
  { date: 10, label: "10", title: ["AM Train", "Evening Market Tour"], city: "PARIS" },
  { date: 11, label: "11", title: ["Strasbourg Day Trip", "Included"], city: "PARIS" },
  { date: 12, label: "12", title: ["Museum Optional", "Market Tour"], city: "PARIS" },

  // Row 3: Dec 13 – Dec 19
  { date: 13, label: "13", title: ["FLY HOME"], city: null, departing: true },
  { date: 14, label: "14", muted: true },
  { date: 15, label: "15", muted: true },
  { date: 16, label: "16", muted: true },
  { date: 17, label: "17", muted: true },
  { date: 18, label: "18", muted: true },
  { date: 19, label: "19", muted: true },
];

const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const CalendarView = () => {
  return (
    <section id="calendar" className="bg-[#1a3a2e] py-20 sm:py-24 px-4 sm:px-8 relative overflow-hidden">
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #e9c987 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <Reveal>
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-[#e9c987]/15 border border-[#e9c987]/40 flex items-center justify-center">
                <Plane className="w-6 h-6 text-[#e9c987]" strokeWidth={1.5} />
              </div>
              <div className="leading-tight">
                <p className="font-script text-[#e9c987] text-2xl leading-none">at a glance</p>
                <p className="text-[#faf5ec]/70 text-xs tracking-[0.3em] uppercase mt-1">Beyond the Path</p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <h2 className="font-display text-[#faf5ec] text-3xl sm:text-4xl md:text-5xl leading-tight uppercase tracking-wide">
                Christmas Market Tour
              </h2>
              <p className="font-display text-[#e9c987] text-2xl sm:text-3xl md:text-4xl tracking-wide mt-1 italic">
                December 3rd – 13th, 2026
              </p>
            </div>
          </div>
        </Reveal>

        {/* Weekday headers */}
        <Reveal>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-2">
            {weekdays.map((d) => (
              <div
                key={d}
                className="bg-[#faf5ec] text-[#1a3a2e] text-center py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.18em] rounded-sm"
              >
                {d}
              </div>
            ))}
          </div>
        </Reveal>

        {/* Calendar grid */}
        <Reveal>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {days.map((d, i) => {
              const isTrip = !d.muted;
              return (
                <div
                  key={i}
                  className={`relative aspect-[3/4] sm:aspect-[4/5] p-1.5 sm:p-2.5 border border-dashed rounded-sm flex flex-col ${
                    isTrip
                      ? "border-[#faf5ec]/55 bg-[#1a3a2e]"
                      : "border-[#faf5ec]/15 bg-transparent"
                  }`}
                >
                  {/* Day number */}
                  <div
                    className={`font-display leading-none ${
                      d.muted
                        ? "text-[#faf5ec]/30 text-base sm:text-2xl"
                        : "text-[#faf5ec] text-lg sm:text-3xl"
                    }`}
                  >
                    {d.label}
                  </div>

                  {/* Activity title */}
                  {d.title && (
                    <div className="mt-1.5 sm:mt-2 flex-1 flex flex-col">
                      <div
                        className={`text-[#faf5ec] leading-tight ${
                          d.departing
                            ? "text-[10px] sm:text-sm font-bold tracking-[0.12em] mt-2"
                            : "text-[8px] sm:text-[11px] font-medium"
                        }`}
                      >
                        {d.title.map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}
                      </div>

                      {/* City badge at bottom */}
                      {d.city && (
                        <div className="mt-auto pt-1">
                          <span className="text-[#e9c987] text-[9px] sm:text-xs font-bold tracking-[0.14em] uppercase">
                            {d.city}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Location summary */}
        <Reveal>
          <div className="mt-10 text-center">
            <div className="inline-flex flex-col sm:flex-row gap-2 sm:gap-8 text-[#faf5ec]/90">
              <span className="font-display text-lg sm:text-xl italic">London, United Kingdom</span>
              <span className="hidden sm:inline-block text-[#e9c987]">•</span>
              <span className="font-display text-lg sm:text-xl italic">Brussels &amp; Bruges, Belgium</span>
              <span className="hidden sm:inline-block text-[#e9c987]">•</span>
              <span className="font-display text-lg sm:text-xl italic">Paris &amp; Strasbourg, France</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default CalendarView;
